import type {
  OpenQuestion,
  OpenQuestionsChangeHandler,
  OpenQuestionsLoader,
  QuestionAddresseeKind,
  QuestionsChangedFrame,
  QuestionsFrameSource,
} from '../types/questions.js';

/**
 * Open questions, newest first, from one list route, kept current by
 * `questions.changed` frames. It reads the route on the initial load, on each
 * frame, and on each transport resync, never on a timer (sessions ADR-c53f5).
 */
export class OpenQuestionsList {
  private _items: OpenQuestion[] = [];
  private changeHandlers = new Set<OpenQuestionsChangeHandler>();
  private loading: Promise<void> | null = null;
  private stale = false;

  constructor(
    private readonly loader: OpenQuestionsLoader,
    private readonly addressees: ReadonlySet<QuestionAddresseeKind>,
  ) {}

  get items(): OpenQuestion[] {
    return this._items;
  }

  /** Read the list route. Called on connect, on every frame and on every resync. */
  async refresh(): Promise<void> {
    if (this.loading) {
      this.stale = true;
      return this.loading;
    }
    this.loading = this.load().finally(() => {
      this.loading = null;
    });
    return this.loading;
  }

  private async load(): Promise<void> {
    do {
      this.stale = false;
      const { questions } = await this.loader();
      this._items = questions;
    } while (this.stale);
    for (const handler of this.changeHandlers) handler(this._items);
  }

  /**
   * Apply one `questions.changed` frame from the bridge. A frame for an
   * addressee kind this list does not hold is ignored.
   */
  async apply(frame: QuestionsChangedFrame): Promise<void> {
    if (!this.addressees.has(frame.addressee_kind)) return;
    if (frame.status !== 'open' && !this._items.some((q) => q.question_id === frame.question_id)) {
      return;
    }
    await this.refresh();
  }

  /**
   * Follow an event transport: apply its `questions.changed` frames and re-read
   * the list on each of its resyncs, so frames missed while it was disconnected
   * never leave the list stale. It re-reads once on attach, so a frame sent
   * between the initial load and this call is not lost. Every failed read
   * reaches `onError`. Returns the unsubscribe.
   */
  follow(source: QuestionsFrameSource, onError: (err: unknown) => void): () => void {
    const offFrames = source.onQuestionsChanged((frame) => {
      this.apply(frame).catch(onError);
    });
    const offResync = source.onQuestionsResync(() => {
      this.refresh().catch(onError);
    });
    this.refresh().catch(onError);
    return () => {
      offFrames();
      offResync();
    };
  }

  onChange(handler: OpenQuestionsChangeHandler): () => void {
    this.changeHandlers.add(handler);
    return () => {
      this.changeHandlers.delete(handler);
    };
  }
}
